import { useEffect, useRef } from 'react';

export default function SplitFlap({ 
  messages, 
  flipSpeed = 80, 
  changeDelay = 4000,
  color = 'inherit',
  length = 10
}) {
  const flipboardRef = useRef(null);
  
  useEffect(() => {
    const CHAR_HEIGHT = 63; // Pixel height of each character in the flipboard

    // Process messages to fit fixed length
    const processedMessages = messages.map(msg => {
      if (length) {
        // Truncate if longer, pad with spaces if shorter
        return msg.substring(0, length).padEnd(length, ' ');
      }
      return msg;
    });

    // Create variables to handle flipboard characters
    const allChars = [...processedMessages.join(''), " "];
    const charList = [...new Set(allChars)].sort();
    
    // Use fixed length if specified, otherwise use the longest message length
    const displayLength = length || Math.max(...processedMessages.map(m => m.length));
    const flipHeight = (charList.length - 1) * CHAR_HEIGHT;

    let currentMessageIndex = 0;

    // Create flipboard
    const flipboard = flipboardRef.current;
    if (!flipboard) return;

    // Generate a unique ID for this instance if not already set
    if (!flipboard.id) {
      flipboard.id = `flipboard-${Math.random().toString(36).substr(2, 9)}`;
    }
    const flipboardId = flipboard.id;
    
    // Add CSS for smooth animations with instance-specific selectors
    const style = document.createElement('style');
    style.textContent = `
      #${flipboardId} .flip {
        display: inline-block;
        position: relative;
        overflow: hidden;
        vertical-align: top;
        color: ${color};
      }
      #${flipboardId} .flip ul {
        list-style: none;
        padding: 0;
        margin: 0;
        position: relative;
        transition: top ${flipSpeed}ms ease-in-out;
      }
      #${flipboardId} .flip li {
        height: ${CHAR_HEIGHT}px;
        line-height: ${CHAR_HEIGHT}px;
        text-align: center;
      }
    `;
    document.head.appendChild(style);

    // Clear existing content
    flipboard.innerHTML = '';

    for (let i = 0; i < displayLength; i++) {
      const flip = document.createElement('div');
      flip.className = 'flip';
      flipboard.appendChild(flip);
    }

    // Initialize flip elements
    const flipElements = document.querySelectorAll(`#${flipboardId} .flip`);
    flipElements.forEach(flip => {
      const ul = document.createElement('ul');
      flip.appendChild(ul);
      flip.setAttribute('value', ' ');
      
      charList.slice().reverse().forEach(char => {
        const li = document.createElement('li');
        li.textContent = char;
        ul.appendChild(li);
      });

      ul.style.top = `${-flipHeight-10}px`;
    });

    // Switch single character with smooth animation
    function switchChar(char, n) {
      const flipElement = flipElements[n];
      if (!flipElement) return;
      
      const current = flipElement.getAttribute('value');
      let i = charList.indexOf(current);
      
      flipElement.setAttribute('value', char);
      const ul = flipElement.querySelector('ul');

      // Reset to top before switching characters
      ul.style.transition = 'none';
      ul.style.top = `${-flipHeight-10}px`;
      // Re-enable transition after a brief moment
      setTimeout(() => {
        ul.style.transition = `top ${flipSpeed}ms ease-in-out`;
      }, 10);
      i = 0;
          
      const animateStep = () => {
        
        if (charList[i] !== char) {
          const currentTop = parseInt(ul.style.top) || 0;
          ul.style.top = `${currentTop + CHAR_HEIGHT}px`;
          i++;
          if (i >= charList.length) {
          }
          setTimeout(animateStep, flipSpeed);
        }
      };
      
      animateStep();
    }
    
    // Switch message
    function displayMessage(message) {
      const msgChars = message.split("");
      
      for (let k = 0; k < displayLength; k++) {
        // Use space for any position beyond the message length
        const char = k < msgChars.length ? msgChars[k] : ' ';
        switchChar(char, k); 
      }
    }

    // Display initial message
    displayMessage(processedMessages[currentMessageIndex]);
    
    // Set up interval for message changes only if there are multiple messages
    let interval;
    if (processedMessages.length > 1) {
      interval = setInterval(() => {
        currentMessageIndex = (currentMessageIndex + 1) % processedMessages.length;
        displayMessage(processedMessages[currentMessageIndex]);
      }, changeDelay);
    }

    // Cleanup function
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      // Remove the added style element
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [messages, flipSpeed, changeDelay, color, length]);

  return (
    <span ref={flipboardRef} className="flipboard inline-block"></span>
  );
}
